export const LoyaltyAuthenticatedContainerComponent = {
    template: `
        <ng-form name="$ctrl.redemptionValidation" ng-if="$ctrl.shouldShow()">
            <loyalty-authenticated
                applied-redemption-amount="$ctrl.getAppliedRedemptionAmount()"
                available-redemption-amount="$ctrl.getAvailableRedemptionAmount()"
                is-redeem-loading="$ctrl.isRedeemLoading()"
                is-redemption-applied="$ctrl.isRedemptionApplied()"
                is-redeem-undo-loading="$ctrl.isRedeemUndoLoading()"
                on-redeem="$ctrl.onRedeem"
                on-redeem-undo="$ctrl.onRedeemUndo"
                remaining-redemption-amount="$ctrl.getRemainingRedemptionAmount()"
            ></loyalty-authenticated>
        </ng-form>
    `,
    controller: class LoyaltyAuthenticatedContainerController {
        constructor(
            $filter,
            $q,
            $timeout,
            accountService,
            calloutService,
            cartService,
            cartStatusChannel,
            googleAnalyticsTrackingService,
            loyaltyService
        ) {
            'ngInject';

            Object.assign(this, {
                $filter,
                $q,
                $timeout,
                accountService,
                calloutService,
                cartService,
                cartStatusChannel,
                googleAnalyticsTrackingService,
                loyaltyService
            });

            this._data = {
                loading: true,
                loyaltyConfig: {},
                redemptionApplied: false
            };

            this._clearLoyaltyData();

            // Need to bind any instance methods that will be passed in to the child component.
            this.onRedeem = this.onRedeem.bind(this);
            this.onRedeemUndo = this.onRedeemUndo.bind(this);
        }

        $onInit() {
            this.loyaltyService.getConfig()
                .then(config => {
                    this._data.loyaltyConfig = config;

                    if (!config.redemptionAllowed) {
                        return this.$q.resolve();
                    }

                    return this._doLookup();
                }).finally(() => {
                    this._data.loading = false;
                });
        }

        _doLookup() {
            return this.loyaltyService.lookup({ skipCallout: true })
                .then(
                    resp => this._lookupSuccess(resp.data),
                    error => this._lookupError(error)
                )
                .finally(() => {
                    this._data.redemptionApplied = this.loyaltyService.isRedemptionAppliedForGuid(this._data.redemptionGuid);
                    this._data.appliedRedemptionAmount = this.loyaltyService.getAppliedRedemptionAmountForGuid(this._data.redemptionGuid);

                    if (this.shouldShow()) {
                        this.googleAnalyticsTrackingService.trackLoyaltyRedemptionVisible();
                    }
                });
        }

        _clearLoyaltyData() {
            Object.assign(this._data, {
                loyaltyAccountExists: false, // As in we successfully did the lookup but might not have found an account.
                redeemAvailable: false,
                redeemLoading: false,
                redeemUndoLoading: false
            });
        }

        _lookupSuccess(resp) {
            this.googleAnalyticsTrackingService.trackLoyaltyRedemption('lookup', true);

            this._data.loyaltyAccountExists = true;
            this._data.availableRedemptionAmount = this.loyaltyService.getAvailableRedemptionAmount(resp);
            this._data.redeemAvailable = this.loyaltyService.isRedemptionAvailable(resp);
            this._data.redemptionGuid = this.loyaltyService.getRedemptionGuid(resp);
            this._lookupRefreshCart(resp);
        }

        _lookupError() {
            this.googleAnalyticsTrackingService.trackLoyaltyRedemption('lookup', false);

            this._clearLoyaltyData();
            return this.$q.resolve();
        }

        /**
         * Refreshes the cart if a logged in loyalty user signs out and signs
         * back in with a different loyalty account. The cart will remove any
         * redemptions applied from the previous loyalty account. This should
         * not happen often since this is an edge case.
         *
         * @param {*} resp a LoyaltyInquiryEnvelope response from `lookup()`
         */
        _lookupRefreshCart(resp) {
            const newloyaltyIdentifier = this.loyaltyService.getLoyaltyIdentifier(resp);
            const currentLoyaltyIdentifier = this.cartService.data.cart.order.checks[0].appliedLoyaltyInfo.loyaltyIdentifier;
            if (currentLoyaltyIdentifier !== newloyaltyIdentifier) {
                // Ensure that we get the new cart from backend
                this.cartService.clearCart();
                this.cartService.getCart().then(() => this.cartStatusChannel.changedCart());
            }
        }

        shouldShow() {
            return this._data.loyaltyConfig.redemptionAllowed
                && this.isRedeemAvailable()
                && !this._data.loading
                && this._data.loyaltyAccountExists;
        }

        isRedeemAvailable() {
            return this._data.redeemAvailable;
        }

        isRedeemLoading() {
            return this._data.redeemLoading;
        }

        isRedemptionApplied() {
            return this._data.redemptionApplied;
        }

        isRedeemUndoLoading() {
            return this._data.redeemUndoLoading;
        }

        getAppliedRedemptionAmount() {
            return this._data.appliedRedemptionAmount;
        }

        getRemainingRedemptionAmount() {
            return this._data.availableRedemptionAmount - this._data.appliedRedemptionAmount;
        }

        getAvailableRedemptionAmount() {
            return this._data.availableRedemptionAmount;
        }

        onRedeem() {
            this._data.redeemLoading = true;
            // disable place order button while processing redemption
            this.redemptionValidation.$setValidity('button-redeem-loyalty', false);

            this.loyaltyService.applyRedemption({
                redemptionGuid: this._data.redemptionGuid,
                skipCallout: true
            }).then(
                resp => this._redemptionSuccess(resp.data),
                error => this._redemptionError(error)
            ).finally(() => {
                this._data.redeemLoading = false;
                this.redemptionValidation.$setValidity('button-redeem-loyalty', true);
            });
        }

        onRedeemUndo() {
            this._data.redeemUndoLoading = true;
            // disable place order button while processing redemption
            this.redemptionValidation.$setValidity('button-redeem-loyalty', false);

            this.loyaltyService.removeLoyaltyRedemption({
                redemptionGuid: this._data.redemptionGuid,
                skipCallout: true
            }).then(
                resp => this._removeLoyaltyRedemptionSuccess(resp.data),
                error => this._removeLoyaltyRedemptionError(error)
            ).finally(() => {
                this._data.redeemUndoLoading = false;
                this.redemptionValidation.$setValidity('button-redeem-loyalty', true);
            });
        }

        _redemptionSuccess(resp) {
            this.googleAnalyticsTrackingService.trackLoyaltyRedemption('apply', true);

            this._data.redemptionApplied = true;
            this.cartService.setCart(resp);
            this.cartStatusChannel.changedCart();
            this._data.appliedRedemptionAmount = this.loyaltyService.getAppliedRedemptionAmount(this._data.redemptionGuid);
            this.calloutService.info('Rewards applied');
        }

        _redemptionError() {
            this.googleAnalyticsTrackingService.trackLoyaltyRedemption('apply', false);

            this.calloutService.error('There was an issue applying your reward, please try again');
        }

        _removeLoyaltyRedemptionSuccess(resp) {
            this.googleAnalyticsTrackingService.trackLoyaltyRedemption('undo', true);

            this._data.redemptionApplied = false;
            this.cartService.setCart(resp);
            this.cartStatusChannel.changedCart();
        }

        _removeLoyaltyRedemptionError() {
            this.googleAnalyticsTrackingService.trackLoyaltyRedemption('undo', false);

            this.calloutService.error('There was an issue removing your reward, please try again.');
        }
    }
};
